/**
 * E-commerce Platform Auto-Detection & Event Tracking
 * Supports: Ticimax, Shopify, WooCommerce, Magento, OpenCart, PrestaShop, Pulpo VTO
 */

import TicimaxTracker from './ticimax';
import ShopifyTracker from './shopify';
import WooCommerceTracker from './woocommerce';
import GenericEcommerceTracker from './generic';
import PulpoTracker from './pulpo';

export default class PlatformDetector {
  constructor(b2mTracker) {
    this.b2mTracker = b2mTracker;
    this.detectedPlatform = null;
    this.platformTracker = null;
    this.isInitialized = false;
    
    // Platform detection signatures
    this.platformSignatures = {
      ticimax: {
        name: 'Ticimax',
        detect: () => this.detectTicimax(),
        tracker: TicimaxTracker
      },
      shopify: {
        name: 'Shopify',
        detect: () => this.detectShopify(),
        tracker: ShopifyTracker
      },
      woocommerce: {
        name: 'WooCommerce',
        detect: () => this.detectWooCommerce(),
        tracker: WooCommerceTracker
      },
      magento: {
        name: 'Magento',
        detect: () => this.detectMagento(),
        tracker: GenericEcommerceTracker
      },
      opencart: {
        name: 'OpenCart',
        detect: () => this.detectOpenCart(),
        tracker: GenericEcommerceTracker
      },
      prestashop: {
        name: 'PrestaShop',
        detect: () => this.detectPrestaShop(),
        tracker: GenericEcommerceTracker
      }
    };
  }

  /**
   * Initialize platform detection and setup tracking.
   *
   * @param {string|null} customEventLib - Value of data-custom-event-lib attribute.
   *   When set to 'pulpo', skip generic platform detection and start the Pulpo VTO
   *   auto-tracker directly.
   */
  init(customEventLib = null) {
    if (this.isInitialized) return;

    // ── Custom event library override ──────────────────────────────────────
    if (customEventLib) {
      const lib = customEventLib.trim().toLowerCase();

      if (lib === 'pulpo') {
        console.log('[B2M Platform] custom-event-lib=pulpo → starting Pulpo VTO auto-tracker');
        this.platformTracker = new PulpoTracker(this.b2mTracker);
        this.platformTracker.init();
        this.detectedPlatform = { key: 'pulpo', name: 'Pulpo', detectionMethod: 'custom_event_lib' };
        this.isInitialized = true;
        return;
      }

      console.warn(`[B2M Platform] Unknown custom-event-lib "${customEventLib}", falling back to auto-detection`);
    }

    // ── Auto-detection ─────────────────────────────────────────────────────
    console.log('[B2M Platform] Starting e-commerce platform detection...');
    
    // Detect platform
    this.detectedPlatform = this.detectPlatform();
    
    if (this.detectedPlatform) {
      console.log(`[B2M Platform] ✅ Detected: ${this.detectedPlatform.name}`);
      
      // Initialize platform-specific tracker
      const TrackerClass = this.detectedPlatform.tracker;
      this.platformTracker = new TrackerClass(this.b2mTracker, this.detectedPlatform.name);
      this.platformTracker.init();
      
      // Track platform detection event
      this.b2mTracker.track('platform_detected', {
        platform_name: this.detectedPlatform.name,
        platform_version: this.detectedPlatform.version || 'unknown',
        detection_method: this.detectedPlatform.detectionMethod
      });
    } else {
      console.log('[B2M Platform] No specific platform detected, using generic e-commerce tracking');
      this.platformTracker = new GenericEcommerceTracker(this.b2mTracker, 'Generic');
      this.platformTracker.init();
    }
    
    this.isInitialized = true;
  }

  /**
   * Detect which e-commerce platform is being used
   */
  detectPlatform() {
    for (const [key, platform] of Object.entries(this.platformSignatures)) {
      const result = platform.detect();
      if (result) {
        return {
          key,
          name: platform.name,
          tracker: platform.tracker,
          version: result.version,
          detectionMethod: result.method
        };
      }
    }
    return null;
  }

  /**
   * Detect Ticimax platform
   */
  detectTicimax() {
    // Check for Ticimax-specific globals
    if (window.ticimax || window.Ticimax) {
      return { method: 'global_object', version: window.ticimax?.version };
    }
    
    // Check for Ticimax meta tags
    const metaGenerator = document.querySelector('meta[name="generator"]');
    if (metaGenerator?.content?.toLowerCase().includes('ticimax')) {
      return { method: 'meta_tag', version: metaGenerator.content };
    }
    
    // Check for Ticimax-specific CSS classes
    const ticimaxElements = document.querySelectorAll('.ticimax-header, .ticimax-footer, [class*="ticimax"]');
    if (ticimaxElements.length > 0) {
      return { method: 'css_classes' };
    }
    
    // Check for Ticimax-specific URLs/scripts
    const scripts = document.querySelectorAll('script[src*="ticimax"]');
    if (scripts.length > 0) {
      return { method: 'script_src' };
    }
    
    // Check for Ticimax dataLayer structure
    if (window.dataLayer) {
      const hasTicimaxData = window.dataLayer.some(item => 
        item.platform === 'ticimax' || 
        item.ecommerce?.platform === 'ticimax'
      );
      if (hasTicimaxData) {
        return { method: 'data_layer' };
      }
    }
    
    // Check for common Ticimax URL patterns
    if (window.location.hostname.includes('ticimax') || 
        document.querySelector('[data-ticimax]')) {
      return { method: 'url_pattern' };
    }
    
    // Check for Ticimax-specific DOM structure
    const ticimaxDomPatterns = [
      '.urun-detay',
      '.sepet-sayfa',
      '.odeme-sayfa',
      '#ticimax-cart',
      '[data-urun-id]'
    ];
    
    for (const selector of ticimaxDomPatterns) {
      if (document.querySelector(selector)) {
        return { method: 'dom_structure' };
      }
    }
    
    return null;
  }

  /**
   * Detect Shopify platform
   */
  detectShopify() {
    // Check for Shopify global object
    if (window.Shopify) {
      return { 
        method: 'global_object', 
        version: window.Shopify.theme?.name || window.Shopify.shop 
      };
    }
    
    // Check for Shopify meta tags
    const shopifyMeta = document.querySelector('meta[name="shopify-checkout-api-token"]');
    if (shopifyMeta) {
      return { method: 'meta_tag' };
    }
    
    // Check for Shopify-specific scripts
    const shopifyScripts = document.querySelectorAll('script[src*="shopify"], script[src*="cdn.shopify"]');
    if (shopifyScripts.length > 0) {
      return { method: 'script_src' };
    }
    
    // Check for Shopify-specific link tags
    const shopifyLinks = document.querySelectorAll('link[href*="shopify"], link[href*="cdn.shopify"]');
    if (shopifyLinks.length > 0) {
      return { method: 'link_href' };
    }
    
    // Check for myshopify.com domain
    if (window.location.hostname.includes('myshopify.com')) {
      return { method: 'domain' };
    }
    
    return null;
  }

  /**
   * Detect WooCommerce platform
   */
  detectWooCommerce() {
    // Check for WooCommerce body classes
    if (document.body.classList.contains('woocommerce') || 
        document.body.classList.contains('woocommerce-page')) {
      return { method: 'body_class' };
    }
    
    // Check for WooCommerce-specific elements
    const wooElements = document.querySelectorAll('.woocommerce, .wc-block-grid, [class*="woocommerce"]');
    if (wooElements.length > 0) {
      return { method: 'css_classes' };
    }
    
    // Check for wc_add_to_cart_params
    if (window.wc_add_to_cart_params || window.woocommerce_params) {
      return { method: 'global_object' };
    }
    
    // Check for WooCommerce scripts
    const wooScripts = document.querySelectorAll('script[src*="woocommerce"], script[src*="wc-"]');
    if (wooScripts.length > 0) {
      return { method: 'script_src' };
    }
    
    return null;
  }

  /**
   * Detect Magento platform
   */
  detectMagento() {
    // Check for Magento global objects
    if (window.Mage || window.require?.s?.contexts?._.config?.baseUrl?.includes('static')) {
      return { method: 'global_object' };
    }
    
    // Check for Magento-specific cookies
    if (document.cookie.includes('mage-') || document.cookie.includes('PHPSESSID')) {
      const magentoElements = document.querySelectorAll('[data-mage-init], .page-wrapper');
      if (magentoElements.length > 0) {
        return { method: 'dom_and_cookies' };
      }
    }
    
    // Check for Magento scripts
    const magentoScripts = document.querySelectorAll('script[src*="mage/"], script[src*="Magento_"]');
    if (magentoScripts.length > 0) {
      return { method: 'script_src' };
    }
    
    return null;
  }

  /**
   * Detect OpenCart platform
   */
  detectOpenCart() {
    // Check for OpenCart-specific elements
    if (document.querySelector('#cart, .cart-content') && 
        document.querySelector('[class*="opencart"]')) {
      return { method: 'css_classes' };
    }
    
    // Check for OpenCart routes in URL
    if (window.location.href.includes('route=') && 
        (window.location.href.includes('product/') || window.location.href.includes('checkout/'))) {
      return { method: 'url_pattern' };
    }
    
    return null;
  }

  /**
   * Detect PrestaShop platform
   */
  detectPrestaShop() {
    // Check for PrestaShop global object
    if (window.prestashop) {
      return { method: 'global_object', version: window.prestashop.version };
    }
    
    // Check for PrestaShop-specific elements
    const prestaElements = document.querySelectorAll('[id*="prestashop"], [class*="prestashop"]');
    if (prestaElements.length > 0) {
      return { method: 'css_classes' };
    }
    
    // Check for PrestaShop meta generator
    const metaGenerator = document.querySelector('meta[name="generator"]');
    if (metaGenerator?.content?.toLowerCase().includes('prestashop')) {
      return { method: 'meta_tag' };
    }
    
    return null;
  }

  /**
   * Get detected platform info
   */
  getPlatformInfo() {
    return {
      detected: !!this.detectedPlatform,
      platform: this.detectedPlatform?.name || 'Generic',
      version: this.detectedPlatform?.version || 'unknown',
      detectionMethod: this.detectedPlatform?.detectionMethod || 'none'
    };
  }

  /**
   * Manually set platform (override auto-detection)
   */
  setPlatform(platformKey) {
    const platform = this.platformSignatures[platformKey];
    if (platform) {
      this.detectedPlatform = {
        key: platformKey,
        name: platform.name,
        tracker: platform.tracker,
        detectionMethod: 'manual'
      };
      
      // Re-initialize tracker
      const TrackerClass = platform.tracker;
      this.platformTracker = new TrackerClass(this.b2mTracker, platform.name);
      this.platformTracker.init();
      
      console.log(`[B2M Platform] Manually set to: ${platform.name}`);
    }
  }
}
